import { REGEX_PUNCTUATION_WITHOUT_SPACE } from '@/common/constants/regex/typographyRegex';

export const TABLE_AGGREGATED_WORKER_THRESHOLD = 500;

type TPrepareTableDataParams = {
  items: unknown[];
  isMore: boolean;
  prevItems: unknown[];
};

type TPrepareTableDataResult = {
  items: unknown[];
};

type TWorkerBaseEventData = {
  requestId: number;
  success: boolean;
} & (TWorkerSuccessData | TWorkerErrorData);

type TWorkerSuccessData = {
  success: true;
  items: unknown[];
};

type TWorkerErrorData = {
  success: false;
  error: string;
};

type TPendingRequest = {
  resolve: (result: TPrepareTableDataResult) => void;
  reject: (error: Error) => void;
};

let worker: Nullable<Worker> = null;
const pendingRequests = new Map<number, TPendingRequest>();
let requestIdCounter = 0;

const getWorker = (): Worker => {
  if (worker) return worker;

  worker = new Worker('/workers/tableAggregatedWorker.js');

  worker.onmessage = (event: MessageEvent<TWorkerBaseEventData>) => {
    const { requestId, success } = event.data;

    const pending = pendingRequests.get(requestId);
    if (!pending) return;

    pendingRequests.delete(requestId);

    if (success) {
      pending.resolve({ items: event.data.items });
      return;
    }

    pending.reject(new Error(event.data.error ?? 'Table aggregated worker failed'));
  };

  worker.onerror = (event: ErrorEvent) => {
    const error = new Error(event.message || 'Table aggregated worker error');
    pendingRequests.forEach((pending) => pending.reject(error));
    pendingRequests.clear();
    worker?.terminate();
    worker = null;
  };

  return worker;
};

export const prepareTableDataInWorker = ({
  items,
  isMore,
  prevItems,
}: TPrepareTableDataParams): Promise<TPrepareTableDataResult> => {
  const requestId = ++requestIdCounter;
  const workerInstance = getWorker();

  return new Promise((resolve, reject) => {
    pendingRequests.set(requestId, { resolve, reject });

    workerInstance.postMessage({
      requestId,
      payload: {
        items,
        isMore,
        prevItems,
        regexSource: REGEX_PUNCTUATION_WITHOUT_SPACE.source,
        regexFlags: REGEX_PUNCTUATION_WITHOUT_SPACE.flags,
      },
    });
  });
};

export const cancelPendingTableDataPreparation = (): void => {
  pendingRequests.forEach((pending) => pending.reject(new Error('Table data preparation cancelled')));
  pendingRequests.clear();
};

export const terminateTableAggregatedWorker = (): void => {
  cancelPendingTableDataPreparation();
  worker?.terminate();
  worker = null;
};

export const shouldUseTableAggregatedWorker = (itemCount: number, isMore: boolean, prevItemCount: number): boolean => {
  const totalCount = isMore ? prevItemCount + itemCount : itemCount;
  return totalCount >= TABLE_AGGREGATED_WORKER_THRESHOLD;
};
